/** 选区是否在指定元素内且非折叠 */
export function getSelectionRect(container) {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
		return null;
	}

	const range = selection.getRangeAt(0);
	if (!container.contains(range.commonAncestorContainer)) {
		return null;
	}

	return range.getBoundingClientRect();
}

export function queryFormatState() {
	return {
		bold: document.queryCommandState('bold'),
		italic: document.queryCommandState('italic'),
		underline: document.queryCommandState('underline'),
		fontSize: getSelectionFontSize(),
		textAlign: getSelectionTextAlign(),
	};
}

export function getSelectionTextAlign() {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) return 'left';

	let node = selection.anchorNode;
	if (!node) return 'left';
	if (node.nodeType === Node.TEXT_NODE) {
		node = node.parentElement;
	}
	if (!node || node.nodeType !== Node.ELEMENT_NODE) return 'left';

	const textAlign = window.getComputedStyle(node).textAlign;
	if (textAlign === 'center') return 'center';
	if (textAlign === 'right' || textAlign === 'end') return 'right';
	if (textAlign === 'justify') return 'justify';
	return 'left';
}

export function execTextAlign(align) {
	const commandMap = {
		left: 'justifyLeft',
		center: 'justifyCenter',
		right: 'justifyRight',
		justify: 'justifyFull',
	};
	const command = commandMap[align];
	if (command) {
		document.execCommand(command, false, null);
	}
}

export function getSelectionFontSize() {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) return null;

	let node = selection.anchorNode;
	if (!node) return null;
	if (node.nodeType === Node.TEXT_NODE) {
		node = node.parentElement;
	}
	if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;

	const fontSize = window.getComputedStyle(node).fontSize;
	return fontSize && fontSize !== 'inherit' ? fontSize : null;
}

export function execFontSize(size) {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) return;

	const range = selection.getRangeAt(0);
	if (range.collapsed) return;

	const span = document.createElement('span');
	span.style.fontSize = normalizeFontSizePx(size);
	span.style.lineHeight = 'inherit';
	span.style.verticalAlign = 'middle';

	const fragment = range.extractContents();
	span.appendChild(fragment);
	range.insertNode(span);

	const nextRange = document.createRange();
	nextRange.selectNodeContents(span);
	selection.removeAllRanges();
	selection.addRange(nextRange);
}

export function normalizeFontSizePx(value, fallback = 18) {
	const num = Number.parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
	if (Number.isNaN(num)) return `${fallback}px`;
	return `${Math.min(72, Math.max(12, num))}px`;
}

export function parseFontSizeNumber(value, fallback = 18) {
	const num = Number.parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
	if (Number.isNaN(num)) return fallback;
	return Math.min(72, Math.max(12, num));
}

export function execFormat(command, value = null) {
	document.execCommand(command, false, value);
}

/** 用 span 包裹选区设色；避免 foreColor 在父级强制 color 时失效 */
export function execTextColor(color) {
	wrapSelectionWithStyle((span) => {
		span.style.color = normalizeHexColor(color);
	});
}

export function execBackgroundColor(color) {
	if (color === 'transparent') {
		wrapSelectionWithStyle((span) => {
			span.style.backgroundColor = 'transparent';
		});
		return;
	}
	wrapSelectionWithStyle((span) => {
		span.style.backgroundColor = normalizeHexColor(color);
	});
}

function wrapSelectionWithStyle(applyStyle) {
	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0) return;

	const range = selection.getRangeAt(0);
	if (range.collapsed) return;

	const span = document.createElement('span');
	applyStyle(span);

	const fragment = range.extractContents();
	span.appendChild(fragment);
	range.insertNode(span);

	const nextRange = document.createRange();
	nextRange.selectNodeContents(span);
	selection.removeAllRanges();
	selection.addRange(nextRange);
}

export function normalizeHexColor(color) {
	if (!color) return '#333333';
	const value = String(color).trim().toLowerCase();
	if (/^#[0-9a-f]{6}$/.test(value)) return value;
	if (/^#[0-9a-f]{3}$/.test(value)) {
		const [, r, g, b] = value;
		return `#${r}${r}${g}${g}${b}${b}`;
	}
	return '#333333';
}

/** 色块描边：浅色用较深边框，避免与白色面板背景融为一体 */
export function getSwatchBorderColor(color) {
	const normalized = normalizeHexColor(color).replace('#', '');
	const r = parseInt(normalized.slice(0, 2), 16);
	const g = parseInt(normalized.slice(2, 4), 16);
	const b = parseInt(normalized.slice(4, 6), 16);
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	if (luminance >= 0.72) {
		return '#b0b0b0';
	}
	if (luminance <= 0.22) {
		return 'rgba(255, 255, 255, 0.45)';
	}

	return `rgba(${Math.max(0, r - 36)}, ${Math.max(0, g - 36)}, ${Math.max(0, b - 36)}, 0.75)`;
}

/** 将十六进制颜色与白色混合变浅，ratio 越大越浅 */
export function lightenHexColor(color, ratio = 0.45) {
	const normalized = normalizeHexColor(color).replace('#', '');
	const channels = [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16));
	const mixed = channels.map((channel) => Math.round(channel + (255 - channel) * ratio));
	return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

/** 十六进制转 rgba */
export function hexToRgba(color, alpha = 1) {
	const normalized = normalizeHexColor(color).replace('#', '');
	const r = parseInt(normalized.slice(0, 2), 16);
	const g = parseInt(normalized.slice(2, 4), 16);
	const b = parseInt(normalized.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const TEXT_COLOR_PRESETS = [
	'#333333',
	'#666666',
	'#999999',
	'#285af9',
	'#e74c3c',
	'#f39c12',
	'#27ae60',
	'#9b59b6',
];

export const BG_COLOR_PRESETS = [
	'transparent',
	'#fff566',
	'#ffd666',
	'#ffadd2',
	'#b7eb8f',
	'#91d5ff',
	'#d3adf7',
	'#ffe7ba',
];

export const FONT_SIZE_PRESETS = [
	{ label: '12', value: '12px' },
	{ label: '14', value: '14px' },
	{ label: '16', value: '16px' },
	{ label: '18', value: '18px' },
	{ label: '20', value: '20px' },
	{ label: '24', value: '24px' },
	{ label: '28', value: '28px' },
	{ label: '32', value: '32px' },
];
